import { DataTable } from '@/shared/components/ui';
import { useState } from 'react';
import styles from './DataTablesPage.module.css';

// Datos de ejemplo
interface Usuario {
    id: number;
    nombre: string;
    email: string;
    rol: string;
    estado: 'Activo' | 'Inactivo';
}

const usuariosEjemplo: Usuario[] = [
    { id: 1, nombre: 'Juan Pérez', email: 'juan@ejemplo.com', rol: 'Administrador', estado: 'Activo' },
    { id: 2, nombre: 'María García', email: 'maria@ejemplo.com', rol: 'Usuario', estado: 'Activo' },
    { id: 3, nombre: 'Carlos López', email: 'carlos@ejemplo.com', rol: 'Usuario', estado: 'Inactivo' },
    { id: 4, nombre: 'Ana Martínez', email: 'ana@ejemplo.com', rol: 'Editor', estado: 'Activo' },
    { id: 5, nombre: 'Luis Rodríguez', email: 'luis@ejemplo.com', rol: 'Usuario', estado: 'Activo' },
];

export const DataTablesPage = () => {
    const [isLoading, setIsLoading] = useState(false);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>DataTables</h1>
                <p className={styles.description}>
                    Componente DataTable genérico para mostrar datos tabulares con soporte para
                    columnas custom, estados de carga y personalización completa.
                </p>
            </div>

            {/* Tabla Básica */}
            <section className={styles.section}>
                <h2>Tabla Básica</h2>
                <p>Tabla simple con datos y nombres de columnas.</p>

                <div className={styles.example}>
                    <DataTable
                        data={usuariosEjemplo}
                        columns={[
                            { key: 'id', header: 'ID' },
                            { key: 'nombre', header: 'Nombre' },
                            { key: 'email', header: 'Email' },
                            { key: 'rol', header: 'Rol' },
                        ]}
                    />

                    <code className={styles.code}>
                        {`<DataTable
  data={usuarios}
  columns={[
    { key: 'id', header: 'ID' },
    { key: 'nombre', header: 'Nombre' },
    { key: 'email', header: 'Email' },
    { key: 'rol', header: 'Rol' },
  ]}
/>`}
                    </code>
                </div>
            </section>

            {/* Tabla con Render Custom */}
            <section className={styles.section}>
                <h2>Tabla con Render Personalizado</h2>
                <p>Usa la prop <code>render</code> para personalizar cómo se muestra cada celda.</p>

                <div className={styles.example}>
                    <DataTable
                        data={usuariosEjemplo}
                        columns={[
                            { key: 'nombre', header: 'Nombre' },
                            { key: 'email', header: 'Email' },
                            {
                                key: 'estado',
                                header: 'Estado',
                                render: (value: string) => (
                                    <span className={`${styles.badge} ${value === 'Activo' ? styles.badgeSuccess : styles.badgeWarning}`}>
                                        {value}
                                    </span>
                                )
                            },
                            {
                                key: 'acciones',
                                header: 'Acciones',
                                render: (_: any, _row: Usuario) => (
                                    <div className={styles.actions}>
                                        <button className={styles.btnEdit}>Editar</button>
                                        <button className={styles.btnDelete}>Eliminar</button>
                                    </div>
                                )
                            },
                        ]}
                    />

                    <code className={styles.code}>
                        {`<DataTable
  data={usuarios}
  columns={[
    { key: 'nombre', header: 'Nombre' },
    { 
      key: 'estado', 
      header: 'Estado',
      render: (value) => (
        <span className={value === 'Activo' ? 'badge-success' : 'badge-warning'}>
          {value}
        </span>
      )
    },
    { 
      key: 'acciones', 
      header: 'Acciones',
      render: (_, row) => (
        <button onClick={() => edit(row)}>Editar</button>
      )
    },
  ]}
/>`}
                    </code>
                </div>
            </section>

            {/* Estado de Carga */}
            <section className={styles.section}>
                <h2>Estado de Carga</h2>
                <p>Muestra un indicador mientras se cargan los datos.</p>

                <div className={styles.example}>
                    <div className={styles.controls}>
                        <button
                            className={styles.btnToggle}
                            onClick={() => setIsLoading(!isLoading)}
                        >
                            {isLoading ? 'Ocultar Loading' : 'Mostrar Loading'}
                        </button>
                    </div>

                    <DataTable
                        data={usuariosEjemplo}
                        columns={[
                            { key: 'nombre', header: 'Nombre' },
                            { key: 'email', header: 'Email' },
                        ]}
                        loading={isLoading}
                    />

                    <code className={styles.code}>
                        {`<DataTable
  data={usuarios}
  columns={columns}
  loading={isLoading}  // 👈 Muestra "Cargando..."
/>`}
                    </code>
                </div>
            </section>

            {/* Tabla Vacía */}
            <section className={styles.section}>
                <h2>Tabla sin Datos</h2>
                <p>Cuando no hay datos, muestra un mensaje informativo.</p>

                <div className={styles.example}>
                    <DataTable
                        data={[]}
                        columns={[
                            { key: 'nombre', header: 'Nombre' },
                            { key: 'email', header: 'Email' },
                            { key: 'rol', header: 'Rol' },
                        ]}
                    />

                    <code className={styles.code}>
                        {`<DataTable
  data={[]}  // 👈 Array vacío
  columns={columns}
/>`}
                    </code>
                </div>
            </section>

            {/* TypeScript Generics */}
            <section className={styles.section}>
                <h2>Tipado con TypeScript</h2>
                <p>DataTable usa generics de TypeScript para tipar correctamente los datos.</p>

                <code className={styles.code}>
                    {`// Definir interfaz de tus datos
interface Usuario {
  id: number;
  nombre: string;
  email: string;
  rol: string;
}

// DataTable inferirá el tipo automáticamente
<DataTable<Usuario>
  data={usuarios}
  columns={[
    { key: 'id', header: 'ID' },
    { key: 'nombre', header: 'Nombre' },
    // TypeScript autocompleta las keys 👆
  ]}
/>`}
                </code>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Default</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>data</code></td>
                                <td><code>T[]</code></td>
                                <td>-</td>
                                <td>Array de datos a mostrar (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>columns</code></td>
                                <td><code>Column&lt;T&gt;[]</code></td>
                                <td>-</td>
                                <td>Definición de columnas (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>loading</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si la tabla está cargando datos</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3 style={{ marginTop: '2rem', marginBottom: '1rem' }}>Column Props</h3>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>key</code></td>
                                <td><code>keyof T | string</code></td>
                                <td>Clave del dato a mostrar</td>
                            </tr>
                            <tr>
                                <td><code>header</code></td>
                                <td><code>string</code></td>
                                <td>Texto del encabezado de la columna</td>
                            </tr>
                            <tr>
                                <td><code>render</code></td>
                                <td><code>(value, row) =&gt; React.ReactNode</code></td>
                                <td>Función opcional para renderizado custom</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { DataTable } from '@/shared/components/ui';
import type { Column } from '@/shared/components/ui';`}
                </code>
            </section>
        </div>
    );
};
